/**
 * セッション / トークン有効期限の管理ユーティリティ。
 *
 * auth-relay のレスポンスに含まれる `expiresIn`（秒）を絶対期限（ms）として保存し、
 * フロント側の唯一の真実（source of truth）として expiry 判定に用いる。
 */

const EXPIRES_AT_KEY = "expiresAt";

// 実際の期限の少し手前で「期限切れ」とみなすための安全マージン（ミリ秒）。
// リクエスト往復中にトークンが切れるのを防ぐ。
const EXPIRY_SKEW_MS = 30 * 1000;

/**
 * `expiresIn`（秒）から絶対期限 `expiresAt`（ms epoch）を計算して保存する。
 * 値が不正（0 / 負数 / NaN / Infinity）な場合は、古い `expiresAt` を削除して
 * 以降を期限切れ扱いにする。
 */
export const saveTokenExpiry = (
  expiresIn: string | number | undefined | null
): void => {
  const seconds = Number(expiresIn);
  // 不正値（0 / 負数 / NaN / Infinity）の場合は古い expiresAt を残さず削除し、
  // 以降は期限切れ扱い（isTokenExpired() が true）になるようにする。
  if (!Number.isFinite(seconds) || seconds <= 0) {
    localStorage.removeItem(EXPIRES_AT_KEY);
    return;
  }
  const expiresAt = Date.now() + seconds * 1000;
  localStorage.setItem(EXPIRES_AT_KEY, expiresAt.toString());
};

/**
 * 保存された絶対期限 `expiresAt`（ms epoch）を返す。
 * 未保存・非有限値の場合は null。refresh スケジューラの予約時刻計算に用いる。
 */
export const getTokenExpiry = (): number | null => {
  const expiresAt = localStorage.getItem(EXPIRES_AT_KEY);
  if (!expiresAt) return null;
  const expiresAtMs = Number(expiresAt);
  return Number.isFinite(expiresAtMs) ? expiresAtMs : null;
};

/**
 * ID Token が期限切れ（または期限間近）かを判定する。
 * `expiresAt` が未保存の場合は安全側に倒して期限切れ扱いとする。
 */
export const isTokenExpired = (): boolean => {
  const expiresAt = localStorage.getItem(EXPIRES_AT_KEY);
  if (!expiresAt) return true;
  const expiresAtMs = Number(expiresAt);
  // 非有限値（NaN / Infinity）は安全側に倒して期限切れ扱いにする
  if (!Number.isFinite(expiresAtMs)) return true;
  return Date.now() >= expiresAtMs - EXPIRY_SKEW_MS;
};

/**
 * 認証関連の localStorage を完全にクリアする。
 * ログアウト / タイムアウト / 401 / refresh 失敗時の後始末で共通利用する。
 */
export const clearAuthStorage = (): void => {
  localStorage.removeItem("idToken");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("lastActive");
  localStorage.removeItem("emailForSignIn");
  localStorage.removeItem(EXPIRES_AT_KEY);
};
